import { useAuth, useOrganization } from '@clerk/clerk-react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { evalKeys } from '@/lib/queryKeys'
import { fetchRelayEvalRuns } from '../api'
import { isActiveEvalRun } from '../evalRunPresentation'
import type { RelayEvalRunsListResponse } from '../types'

const NO_AUTH_TOKEN_ERROR = 'No authentication token available'
const RELAY_EVAL_RUNS_REFETCH_INTERVAL_MS = 3000

export function getRelayEvalRunsRefetchInterval(
  data?: RelayEvalRunsListResponse
): typeof RELAY_EVAL_RUNS_REFETCH_INTERVAL_MS | false {
  if (!data) return RELAY_EVAL_RUNS_REFETCH_INTERVAL_MS
  return data.items.some(isActiveEvalRun)
    ? RELAY_EVAL_RUNS_REFETCH_INTERVAL_MS
    : false
}

export function useRelayEvalRunsQuery(runIds: string[]) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()
  const normalizedRunIds = Array.from(new Set(runIds.filter(Boolean)))

  return useQuery({
    queryKey: evalKeys.runsById(organization?.id, normalizedRunIds),
    enabled: normalizedRunIds.length > 0,
    // Starting a run adds its id to the key; keep the previous runs' data
    // until the refetch lands so existing rows' statuses don't reset.
    placeholderData: keepPreviousData,
    refetchInterval: (query) =>
      getRelayEvalRunsRefetchInterval(query.state.data),
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(NO_AUTH_TOKEN_ERROR)
      return fetchRelayEvalRuns(token, {
        runIds: normalizedRunIds,
        limit: normalizedRunIds.length,
      })
    },
  })
}
